import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, patch, post } from "../../lib/api";
import {
  API_KEY_MAX,
  LABEL_MAX,
  MERCHANT_ID_MAX,
  PEM_MAX,
  type ProviderDraft,
  createBody,
  credentialStatus,
  deleteBlocker,
  emptyDraft,
  providerDraftErrors,
  updateBody,
} from "../../lib/paymentProviders";
import type { PaymentProvider, PaymentProviderEnvironment } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { CONTROL, Field, FieldShell, SelectField } from "../ui/Input";
import { MicroLabel } from "../ui/Text";

/**
 * Adding a merchant account, or editing one.
 *
 * Secret inputs are always empty. On an edit, what is stored is described —
 * "Set · when · by whom" — and never shown, because the server never returns it:
 * typing into the box replaces the key, leaving it blank keeps it. Browsers are
 * told not to remember or suggest anything typed here.
 *
 * The merchant id and environment are locked once the provider has taken orders: those orders
 * refund through it, and a different merchant could not refund them.
 */

export interface ProviderFormProps {
  /** Absent to create. */
  readonly provider?: PaymentProvider;
  readonly timeZone: string;
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}

const SECRET_INPUT = {
  type: "password",
  autoComplete: "new-password",
  spellCheck: false,
  autoCapitalize: "off",
  autoCorrect: "off",
} as const;

export function ProviderForm({ provider, timeZone, onDone, onCancel }: ProviderFormProps) {
  const queryClient = useQueryClient();
  const mode = provider === undefined ? "create" : "edit";
  const [draft, setDraft] = useState<ProviderDraft>(() => emptyDraft(provider));
  const [removeRefundKey, setRemoveRefundKey] = useState(false);
  const [touched, setTouched] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["payment-providers"] });
    void queryClient.invalidateQueries({ queryKey: ["depots"] });
  };

  const save = useMutation({
    // The request carries the typed secrets; keep nothing once the form is gone.
    gcTime: 0,
    mutationFn: async () => {
      if (provider === undefined) {
        return post<PaymentProvider>("/payment-providers", createBody(draft));
      }
      const body = updateBody(provider, draft, removeRefundKey);
      return body === undefined
        ? provider
        : patch<PaymentProvider>(`/payment-providers/${encodeURIComponent(provider.id)}`, body);
    },
    onSuccess: (saved) => {
      refresh();
      onDone(`${saved.label} ${mode === "create" ? "added" : "saved"}.`);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/payment-providers/${encodeURIComponent(id)}`),
    onSuccess: () => {
      refresh();
      onDone(`${provider?.label ?? "Provider"} deleted.`);
    },
  });

  const errors = providerDraftErrors(draft, mode);
  const isValid = Object.keys(errors).length === 0;
  const hasChanges =
    provider === undefined || updateBody(provider, draft, removeRefundKey) !== undefined;
  const merchantFrozen = provider !== undefined && provider.orderCount > 0;
  const blocker = provider === undefined ? undefined : deleteBlocker(provider);
  const heading = provider === undefined ? "New payment provider" : `Edit ${provider.label}`;

  const set = <K extends keyof ProviderDraft>(key: K, value: ProviderDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const shown = (key: keyof ProviderDraft) => (touched ? errors[key] : undefined);

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>{heading}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {save.isError ? <ErrorNotice error={save.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <form
        aria-label={heading}
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (!isValid || !hasChanges || save.isPending) return;
          save.mutate();
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2"
      >
        <Field
          label="Name"
          value={draft.label}
          onChange={(event) => set("label", event.target.value)}
          placeholder="ABA — Phnom Penh depots"
          maxLength={LABEL_MAX}
          required
          autoFocus
          error={shown("label")}
          hint="How operators pick it for a depot. Shown in the audit log."
        />
        <SelectField
          label="Environment"
          value={draft.environment}
          onChange={(event) => set("environment", event.target.value as PaymentProviderEnvironment)}
          disabled={merchantFrozen}
          hint={
            merchantFrozen
              ? "Locked with the merchant id: its orders refund on this gateway."
              : draft.environment === "sandbox"
                ? "ABA's sandbox. No money moves; drivers see a test-payment banner."
                : "ABA's live gateway. Real money."
          }
        >
          <option value="sandbox">Sandbox</option>
          <option value="production">Production</option>
        </SelectField>
        <Field
          label="Merchant id"
          value={draft.merchantId}
          onChange={(event) => set("merchantId", event.target.value)}
          maxLength={MERCHANT_ID_MAX}
          required
          disabled={merchantFrozen}
          spellCheck={false}
          error={shown("merchantId")}
          hint={
            merchantFrozen
              ? "Locked: this merchant has taken orders, and they refund through it. Add a new provider for another merchant."
              : undefined
          }
        />
        <Field
          {...SECRET_INPUT}
          label="API key"
          value={draft.apiKey}
          onChange={(event) => set("apiKey", event.target.value)}
          maxLength={API_KEY_MAX}
          required={provider === undefined}
          placeholder={provider === undefined ? "" : "Leave blank to keep the current key"}
          error={shown("apiKey")}
          hint={
            provider === undefined
              ? "Stored encrypted. Nobody can read it back, including you."
              : `${credentialStatus(provider.apiKey, timeZone)}. Typing a new one replaces it.`
          }
        />
        <div className="md:col-span-2">
          <FieldShell
            label="RSA public key (refunds)"
            error={shown("refundPublicKey")}
            hint={
              provider === undefined || !provider.refundKey.set
                ? "Optional. ABA issues it on request; without it refunds are cleared by hand in the merchant portal and holds cannot be collected."
                : `${credentialStatus(provider.refundKey, timeZone)}. Pasting a new one replaces it.`
            }
            render={({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                value={draft.refundPublicKey}
                onChange={(event) => set("refundPublicKey", event.target.value)}
                maxLength={PEM_MAX}
                rows={4}
                spellCheck={false}
                autoComplete="off"
                disabled={removeRefundKey}
                placeholder="-----BEGIN PUBLIC KEY-----"
                aria-invalid={invalid}
                {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
                className={`${CONTROL} resize-y font-mono text-micro ${invalid ? "border-signal" : ""}`}
              />
            )}
          />
          {provider?.refundKey.set === true ? (
            <label className="mt-2 flex items-start gap-2 text-body text-ink">
              <input
                type="checkbox"
                checked={removeRefundKey}
                onChange={(event) => setRemoveRefundKey(event.target.checked)}
                className="mt-1 size-4 accent-signal"
              />
              <span>
                Remove the RSA key.
                <span className="block text-micro tracking-normal normal-case text-ink-faint">
                  Refunds then queue for an operator, and holds cannot be collected.
                </span>
              </span>
            </label>
          ) : null}
        </div>

        {provider === undefined ? (
          <label className="flex items-start gap-2 text-body text-ink md:col-span-2">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(event) => set("isDefault", event.target.checked)}
              className="mt-1 size-4 accent-signal"
            />
            <span>
              Make it the default.
              <span className="block text-micro tracking-normal normal-case text-ink-faint">
                New depots are given the default. Existing depots keep theirs.
              </span>
            </span>
          </label>
        ) : null}

        <div className="flex flex-wrap items-end gap-4 md:col-span-2">
          <Button
            type="submit"
            variant="primary"
            disabled={save.isPending || !hasChanges}
            pending={save.isPending}
          >
            {provider === undefined ? "Add provider" : "Save provider"}
          </Button>
          {provider === undefined ? null : (
            <>
              {confirmingDelete ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="danger"
                    pending={remove.isPending}
                    aria-label={`Confirm deleting ${provider.label}`}
                    onClick={() => remove.mutate(provider.id)}
                  >
                    Delete now — its keys are erased
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                    Keep
                  </Button>
                </span>
              ) : (
                <Button
                  variant="danger"
                  disabled={blocker !== undefined}
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete provider
                </Button>
              )}
              {blocker === undefined ? null : (
                <span className="text-micro tracking-normal normal-case text-ink-faint">
                  {blocker}
                </span>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}
